import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { CheckCircle2, Circle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function KarmaTasks() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["karma-tasks"],
    queryFn: () => api.get("/agent/karma-tasks").then(r => r.data),
    refetchInterval: 15000,
  });

  const complete = useMutation({
    mutationFn: (id) => api.post(`/karma/${id}/complete`),
    onSuccess: () => { toast.success("Drift task acknowledged"); qc.invalidateQueries({queryKey:["karma-tasks"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <Layout title="Drift Protocol." subtitle="Daily benign engagement · anti-detection">
      <div className="border border-white/10 p-6 mb-6 bg-[#09090B]">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#FF3B30] mt-0.5"/>
          <div>
            <div className="text-[13px] text-zinc-200 mb-1">Complete these two neutral tasks today.</div>
            <div className="text-[12px] text-zinc-500 leading-relaxed">Random, non-branded engagement on unrelated subreddits preserves organic activity signals and shields your account from sudden bans. Also called <em>karma tasks</em> — same thing.</div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {q.data?.length === 0 && (
          <div className="border border-dashed border-white/15 p-12 text-center">
            <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// no drift tasks yet</div>
            <div className="text-zinc-400 text-sm">Ask your admin to trigger today’s drift batch, or check back tomorrow.</div>
          </div>
        )}
        {q.data?.map(k => (
          <div key={k.id} data-testid={`karma-item-${k.id}`} className="border border-white/10 p-4 flex items-center gap-4">
            <button
              onClick={() => !k.completed && complete.mutate(k.id)}
              disabled={k.completed || complete.isPending}
              data-testid={`karma-complete-${k.id}`}
              className="shrink-0"
            >
              {k.completed
                ? <CheckCircle2 className="w-6 h-6 text-emerald-400"/>
                : <Circle className="w-6 h-6 text-zinc-500 hover:text-white"/>}
            </button>
            <div className="flex-1">
              <div className={`text-[14px] ${k.completed ? "text-zinc-500 line-through" : "text-zinc-100"}`}>{k.task}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                {k.completed ? "completed" : "pending"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
